import { HtmlContent } from "@/components/Common/HtmlContent"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Box, Button, Text, VStack } from "@chakra-ui/react"
import { FiRefreshCw } from "react-icons/fi"

interface RationalePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  rationaleHtml: string
  isLoading: boolean
  onAccept: () => void
  onRegenerate: () => void
  provider?: string
  model?: string
  estimatedCost?: number
}

export function RationalePreviewModal({
  isOpen,
  onClose,
  rationaleHtml,
  isLoading,
  onAccept,
  onRegenerate,
  provider,
  model,
  estimatedCost,
}: RationalePreviewModalProps) {
  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      size={{ base: "sm", md: "lg" }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>AI-Generated Rationale Preview</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />

        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="gray.600">
              Review the AI-generated rationale below. You can accept it,
              regenerate a new one, or close this dialog to write your own.
            </Text>

            {/* Preview Box */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              bg="bg.muted"
              minH="150px"
            >
              <HtmlContent content={rationaleHtml} />
            </Box>

            {/* Metadata */}
            {provider && model && (
              <Text fontSize="xs" color="gray.500">
                Generated by {provider} ({model})
                {estimatedCost && ` • Est. cost: $${estimatedCost.toFixed(6)}`}
              </Text>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onRegenerate}
            loading={isLoading}
            disabled={isLoading}
          >
            <FiRefreshCw />
            Regenerate
          </Button>

          <DialogActionTrigger asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogActionTrigger>

          <Button
            colorPalette="blue"
            onClick={onAccept}
            loading={isLoading}
            disabled={isLoading}
          >
            Accept & Use
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
